import {useQueryClient} from "@tanstack/react-query";
import {zodResolver} from "@hookform/resolvers/zod";
import {CheckCircle, CircleX, Clock, Coffee, Utensils} from "lucide-react";
import {useEffect, useRef, useState, type Dispatch, type SetStateAction} from "react";
import {useForm} from "react-hook-form";
import {z} from "zod";

import {Button} from "@/components/ui/button";
import {Checkbox} from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import {Form, FormControl, FormField, FormItem, FormLabel} from "@/components/ui/form";
import FormError from "@/components/ui/form-error";
import {Input} from "@/components/ui/input";
import {Label} from "@/components/ui/label";
import {Textarea} from "@/components/ui/textarea";
import {
  ComplianceGateActionButton,
  ComplianceGateActionError,
  ComplianceGateDialogHeader,
  ComplianceReconcileOverlay,
  CoordinatesRequiredGate,
  complianceGateDialogContentClassName,
} from "@/src/modules/core/components";
import {useToast} from "@/src/modules/core/hooks";
import {useBlockingGate} from "@/src/modules/core/hooks/use-blocking-gate";
import {
  useComplianceGateCoordinates,
  prewarmGateCoordinatesOnPointerDown,
} from "@/src/modules/core/hooks/use-compliance-gate-coordinates";
import {useGateSubformCoordinatesPrewarm} from "@/src/modules/core/hooks/use-coordinates-gate";
import {resolveShiftGpsForSubmit} from "@/src/modules/core/lib/resolve-shift-gps-coordinates";
import {isViewingAsUser} from "@/src/modules/core/lib/impersonation";
import {reconcileCompliance} from "@/src/modules/core/lib/reconcile-compliance";
import {triggerNotificationAttention} from "@/src/modules/notification-feed/lib/notification-attention";
import {beginComplianceGateHandling} from "@/src/modules/notification-feed/lib/dispatch-shift-compliance-gate-from-notification";
import {useStoredMe} from "@/src/modules/core/store/me";
import {useSetBlockingGateReconciling} from "@/src/modules/core/store/blocking-gate";
import {useShiftsStore} from "@/src/modules/shifts/store";
import {Shift, ShiftBreak} from "@/src/modules/shifts/types";

import {useActiveShift, useBreakAction} from "../../hooks";
import {
  getBreakActionErrorTitle,
  isBreakBusinessError,
} from "../../lib/break-action-errors";
import {
  BREAK_ALERT_EVENT_NAMES,
  subscribeWindowEvents,
} from "../../lib/compliance-realtime-events";
import {getBreakNoticeCopy, getBreakNoticeVariant} from "../../lib/break-notice-copy";
import {getShiftBreaks} from "@/src/modules/shifts/lib/shift-breaks";
import {delaySchema} from "../../schema";
import {isBreakPastShiftEndError, officerService} from "../../services";
import {BreakAction, BreakActionRequest} from "../../types";

const MESSAGES = {
  [BreakAction.START]: "Your break has started",
  [BreakAction.COMPLETE]: "Your break has been completed",
  [BreakAction.DELAY]: "Your break has been delayed",
  [BreakAction.INTERRUPT]: "Your break has been interrupted",
  [BreakAction.REJECT]: "Your break has been rejected",
  [BreakAction.TAKEN]: "Your break has been marked as already taken",
};

function readBreakAlertShiftBreakId(detail: unknown): number | undefined {
  const record = detail && typeof detail === "object" && !Array.isArray(detail)
    ? (detail as Record<string, unknown>)
    : null;
  if (!record) {
    return undefined;
  }
  const id = record.shift_break_id;
  if (typeof id === "number" && Number.isFinite(id)) {
    return id;
  }
  if (typeof id === "string") {
    const parsed = Number(id);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

function findAvailableNoticeBreak(
  shift: Shift,
  preferredBreakId?: number,
  trustServerAlert = false,
) {
  const scheduled = getShiftBreaks(shift).filter((b) => b.status === "scheduled");
  if (preferredBreakId != null) {
    const preferred = scheduled.find((b) => b.shift_break_id === preferredBreakId);
    if (preferred) {
      if (trustServerAlert || officerService.getIsBreakAvailable(preferred)) {
        return preferred;
      }
    }
  }
  return scheduled.find(officerService.getIsBreakAvailable);
}

function applyNoticeBreakFromShift(
  shift: Shift | null,
  preferredBreakId: number | undefined,
  setShiftBreak: Dispatch<SetStateAction<ShiftBreak | null>>,
  suppressModal: boolean,
  trustServerAlert = false,
) {
  if (!shift || suppressModal) {
    setShiftBreak((prev) => (prev === null ? prev : null));
    return;
  }
  const availableBreak = findAvailableNoticeBreak(shift, preferredBreakId, trustServerAlert);
  if (availableBreak) {
    setShiftBreak((prev) =>
      prev?.shift_break_id === availableBreak.shift_break_id ? prev : availableBreak,
    );
  } else {
    setShiftBreak((prev) => (prev === null ? prev : null));
  }
}

function readActiveShiftFromCache(queryClient: ReturnType<typeof useQueryClient>) {
  return queryClient.getQueryData<{shift: Shift | null}>(["active-shift"])?.shift ?? null;
}

export function BreakNoticeAlert() {
  const toast = useToast();
  const me = useStoredMe();
  const viewingAsUser = isViewingAsUser(me);
  const queryClient = useQueryClient();
  const {shift: activeShift} = useActiveShift();
  const [shiftBreak, setShiftBreak] = useState<ShiftBreak | null>(null);
  const [actionError, setActionError] = useState<{
    message: string;
    action: BreakAction;
  } | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [gpsBusy, setGpsBusy] = useState(false);
  const {isPending, mutateAsync: breakAction} = useBreakAction();
  const setReconciling = useSetBlockingGateReconciling();

  const present = !viewingAsUser && shiftBreak != null;
  const {isVisible, showReconcilingOverlay} = useBlockingGate({id: "break-notice", present});
  const {submitBlockedByLocation} = useComplianceGateCoordinates(present);
  const lastAttentionKeyRef = useRef<string | null>(null);
  const breakAlertPreferredIdRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!isVisible || viewingAsUser || !shiftBreak?.shift_break_id) {
      return;
    }
    const attentionKey = `break-${shiftBreak.shift_break_id}`;
    if (lastAttentionKeyRef.current === attentionKey) {
      return;
    }
    lastAttentionKeyRef.current = attentionKey;
    triggerNotificationAttention("shift.break_alert", attentionKey);
  }, [isVisible, viewingAsUser, shiftBreak?.shift_break_id]);

  useEffect(() => {
    function handleBreakAutocompleted() {
      void (async () => {
        setReconciling(true, "break-notice");
        try {
          await reconcileCompliance(queryClient);
          setShiftBreak(null);
          breakAlertPreferredIdRef.current = undefined;
        } finally {
          setReconciling(false, "break-notice");
        }
      })();
    }

    window.addEventListener("break:autocompleted", handleBreakAutocompleted);
    return () => {
      window.removeEventListener("break:autocompleted", handleBreakAutocompleted);
    };
  }, [queryClient, setReconciling]);

  useEffect(() => {
    function handleBreakAlert(event: Event) {
      const preferredBreakId = readBreakAlertShiftBreakId(
        (event as CustomEvent<unknown>).detail,
      );
      if (
        preferredBreakId != null &&
        !beginComplianceGateHandling("break", preferredBreakId)
      ) {
        return;
      }

      breakAlertPreferredIdRef.current = preferredBreakId;

      if (preferredBreakId != null) {
        triggerNotificationAttention("shift.break_alert", `break-${preferredBreakId}`);
      }

      void (async () => {
        setReconciling(true, "break-notice");
        try {
          await reconcileCompliance(queryClient);
          const refreshed = readActiveShiftFromCache(queryClient);
          applyNoticeBreakFromShift(
            refreshed,
            preferredBreakId,
            setShiftBreak,
            viewingAsUser,
            preferredBreakId != null,
          );
        } finally {
          setReconciling(false, "break-notice");
        }
      })();
    }

    return subscribeWindowEvents(BREAK_ALERT_EVENT_NAMES, handleBreakAlert);
  }, [queryClient, setReconciling, viewingAsUser]);

  useEffect(() => {
    applyNoticeBreakFromShift(
      activeShift,
      breakAlertPreferredIdRef.current,
      setShiftBreak,
      viewingAsUser,
      breakAlertPreferredIdRef.current != null,
    );
  }, [activeShift, viewingAsUser]);

  useEffect(() => {
    setActionError(null);
  }, [shiftBreak?.shift_break_id]);

  if (!shiftBreak || viewingAsUser) return null;

  const handleBreakAction = async (
    action: BreakAction,
    {reason, delay_minutes}: {reason?: string; delay_minutes?: number} = {},
  ) => {
    setActionError(null);
    setActionBusy(true);
    setGpsBusy(true);
    try {
      const gps = await resolveShiftGpsForSubmit(activeShift, "break_action");
      setGpsBusy(false);
      if (!gps) {
        return;
      }
      const data = {
        shift_break_id: shiftBreak.shift_break_id,
        gps_coordinates: gps.gps_coordinates,
        gps_source: gps.gps_source,
        action,
      } as BreakActionRequest;

      if (reason) {
        data.reason = reason;
      }

      if (delay_minutes) {
        data.delay_minutes = delay_minutes;
      }

      await breakAction(data);
      toast.success(MESSAGES[action]);

      setReconciling(true, "break-notice");
      try {
        await reconcileCompliance(
          queryClient,
          action === BreakAction.DELAY
            ? undefined
            : {shiftBreakId: shiftBreak.shift_break_id},
        );
      } finally {
        setReconciling(false, "break-notice");
      }
      setShiftBreak(null);
      breakAlertPreferredIdRef.current = undefined;
    } catch (e) {
      if (!isBreakBusinessError(e)) return;

      setActionError({message: getBreakActionErrorTitle(e), action});

      if (!isBreakPastShiftEndError(e)) return;

      const refreshedShift = useShiftsStore.getState().activeShift;
      if (!refreshedShift) return;

      queryClient.setQueryData(["active-shift"], {shift: refreshedShift});
      const updatedBreak = getShiftBreaks(refreshedShift).find(
        (b) => b.shift_break_id === shiftBreak.shift_break_id,
      );
      if (updatedBreak) {
        setShiftBreak(updatedBreak);
      }
    } finally {
      setGpsBusy(false);
      setActionBusy(false);
    }
  };

  return (
    <BreakNoticeModal
      open={isVisible}
      showReconcilingOverlay={showReconcilingOverlay}
      isPending={isPending}
      actionBusy={actionBusy}
      gpsBusy={gpsBusy}
      submitBlockedByLocation={submitBlockedByLocation}
      shiftBreak={shiftBreak}
      actionError={actionError}
      onClearActionError={() => setActionError(null)}
      onStart={() => handleBreakAction(BreakAction.START)}
      onDelay={(data) => handleBreakAction(BreakAction.DELAY, data)}
      onReject={() => handleBreakAction(BreakAction.REJECT)}
      onTaken={() => handleBreakAction(BreakAction.TAKEN)}
    />
  );
}

interface BreakNoticeModalProps {
  open: boolean;
  showReconcilingOverlay?: boolean;
  isPending: boolean;
  actionBusy: boolean;
  gpsBusy?: boolean;
  submitBlockedByLocation: boolean;
  shiftBreak: ShiftBreak;
  actionError?: {message: string; action: BreakAction} | null;
  onClearActionError?: () => void;
  onStart: () => void | Promise<void>;
  onDelay: (data: {reason: string; delay_minutes: number}) => void | Promise<void>;
  onReject: () => void | Promise<void>;
  onTaken: () => void | Promise<void>;
}

export function BreakNoticeModal({
  open,
  showReconcilingOverlay,
  isPending,
  actionBusy,
  gpsBusy = false,
  submitBlockedByLocation,
  shiftBreak,
  actionError,
  onClearActionError,
  onStart,
  onDelay,
  onReject,
  onTaken,
}: BreakNoticeModalProps) {
  const isDelayAvailable = officerService.getIsDelayAvailable(shiftBreak);
  const canReject = officerService.getCanReject(shiftBreak);
  const canTakeBreak = officerService.getCanTakeBreak(shiftBreak);
  const copyVariant = getBreakNoticeVariant({
    canReject,
    canTakeBreak,
    isDelayAvailable,
  });
  const copy = getBreakNoticeCopy(shiftBreak.break_type, copyVariant);
  const [showDelayForm, setShowDelayForm] = useState(false);
  useGateSubformCoordinatesPrewarm(true, showDelayForm, submitBlockedByLocation);
  const [isRejectChecked, setIsRejectChecked] = useState(false);
  const [pendingAction, setPendingAction] = useState<
    "start" | "delay" | "reject" | "taken" | null
  >(null);
  const breakIcon = shiftBreak.break_type === "rest" ? Coffee : Utensils;
  const BreakIcon = breakIcon;
  const isBusy = isPending || actionBusy;

  const visibleActionError =
    actionError &&
    (showDelayForm
      ? actionError.action === BreakAction.DELAY
      : actionError.action !== BreakAction.DELAY)
      ? actionError.message
      : null;

  useEffect(() => {
    if (!isBusy) {
      setPendingAction(null);
    }
  }, [isBusy]);

  useEffect(() => {
    if (canReject) {
      setShowDelayForm(false);
      setIsRejectChecked(false);
    }
  }, [canReject]);

  return (
    <Dialog modal open={open} onOpenChange={() => {}}>
      <DialogContent
        className={complianceGateDialogContentClassName}
        showCloseButton={false}
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <div className="flex flex-col gap-4">
          <ComplianceGateDialogHeader
            icon={BreakIcon}
            title={copy.title}
            description={copy.description}
          />

          {submitBlockedByLocation ? <CoordinatesRequiredGate /> : null}

          {!showDelayForm ? (
            <fieldset
              disabled={submitBlockedByLocation}
              className="m-0 flex flex-col gap-4 border-0 p-0"
            >
              {copy.body ? (
                <p className="text-sm leading-relaxed text-muted-foreground">{copy.body}</p>
              ) : null}

              {canReject && copy.acknowledgeLabel ? (
                <div className="flex items-center gap-2">
                  <Label>
                    <Checkbox
                      checked={isRejectChecked}
                      onCheckedChange={(checked) => setIsRejectChecked(checked as boolean)}
                    />{" "}
                    <span className="text-sm font-normal">{copy.acknowledgeLabel}</span>
                  </Label>
                </div>
              ) : null}

              <div className="flex gap-2">
                {canReject ? (
                  <>
                    <ComplianceGateActionButton
                      variant="destructive"
                      className="flex-1 gap-2"
                      isLoading={pendingAction === "reject"}
                      isOtherLoading={isBusy && pendingAction !== "reject"}
                      disabled={!isRejectChecked}
                      onClick={() => {
                        setPendingAction("reject");
                        void onReject();
                      }}
                    >
                      <CircleX className="size-4" />
                      Reject
                    </ComplianceGateActionButton>
                    <ComplianceGateActionButton
                      variant="outline"
                      className="flex-1 gap-2"
                      isLoading={pendingAction === "taken"}
                      isOtherLoading={isBusy && pendingAction !== "taken"}
                      disabled={!isRejectChecked}
                      onClick={() => {
                        setPendingAction("taken");
                        void onTaken();
                      }}
                    >
                      <CheckCircle className="size-4" />
                      Already taken
                    </ComplianceGateActionButton>
                  </>
                ) : (
                  <Button
                    variant="outline"
                    className="flex-1 gap-2"
                    disabled={!isDelayAvailable || isBusy}
                    {...prewarmGateCoordinatesOnPointerDown()}
                    onClick={() => setShowDelayForm(true)}
                  >
                    <Clock className="size-4" />
                    Delay
                  </Button>
                )}
                {canTakeBreak && !canReject ? (
                  <ComplianceGateActionButton
                    className="flex-1 gap-2"
                    isLoading={pendingAction === "start"}
                    isOtherLoading={isBusy && pendingAction !== "start"}
                    loadingLabel={gpsBusy ? "Getting location…" : undefined}
                    onClick={() => {
                      setPendingAction("start");
                      void onStart();
                    }}
                  >
                    <BreakIcon className="size-4" />
                    Take break
                  </ComplianceGateActionButton>
                ) : null}
              </div>
            </fieldset>
          ) : (
            <fieldset disabled={submitBlockedByLocation} className="m-0 border-0 p-0">
              <DelayForm
              isLoading={pendingAction === "delay"}
              isOtherLoading={isBusy && pendingAction !== "delay"}
              onSubmit={(data) => {
                setPendingAction("delay");
                void onDelay({reason: data.reason, delay_minutes: data.delay_minutes});
              }}
              onCancel={() => {
                setShowDelayForm(false);
                onClearActionError?.();
              }}
            />
            </fieldset>
          )}

          {visibleActionError ? (
            <ComplianceGateActionError message={visibleActionError} />
          ) : null}
        </div>
        {showReconcilingOverlay ? <ComplianceReconcileOverlay /> : null}
      </DialogContent>
    </Dialog>
  );
}

interface DelayFormProps {
  isLoading: boolean;
  isOtherLoading: boolean;
  onCancel: () => void;
  onSubmit: (data: z.infer<typeof delaySchema>) => void;
}

type DelayFormValues = z.infer<typeof delaySchema>;

function DelayForm({isLoading, isOtherLoading, onSubmit, onCancel}: DelayFormProps) {
  const form = useForm<DelayFormValues>({
    defaultValues: {
      reason: "",
      delay_minutes: 1,
    },
    resolver: zodResolver(delaySchema),
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <FormField
          control={form.control}
          name="delay_minutes"
          render={({field, formState}) => (
            <FormItem>
              <FormLabel>Delay for</FormLabel>
              <div className="flex w-fit items-center rounded-md focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px]">
                <FormControl className="basis-16">
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    className="rounded-r-none focus-visible:ring-0 focus-visible:border-0"
                    {...field}
                  />
                </FormControl>
                <span className="flex h-9 items-center rounded-md rounded-l-none border border-l-0 bg-muted px-3 py-1 text-sm">
                  minutes
                </span>
              </div>
              {formState.errors.delay_minutes && (
                <FormError error={formState.errors.delay_minutes.message} />
              )}
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="reason"
          render={({field, formState}) => (
            <FormItem>
              <FormLabel>Reason</FormLabel>
              <FormControl>
                <Textarea rows={5} className="resize-none" {...field} />
              </FormControl>
              {formState.errors.reason && <FormError error={formState.errors.reason.message} />}
            </FormItem>
          )}
        />

        <div className="flex justify-end items-center gap-2">
          <Button
            variant="ghost"
            type="button"
            disabled={isLoading || isOtherLoading}
            onClick={onCancel}
          >
            Cancel
          </Button>
          <ComplianceGateActionButton
            type="submit"
            isLoading={isLoading}
            isOtherLoading={isOtherLoading}
          >
            Confirm delay
          </ComplianceGateActionButton>
        </div>
      </form>
    </Form>
  );
}
